import { detectCreditCard } from '@vend/credit-card-detector'
import { useT } from '@transifex/react'
import { useEffect } from 'react'
import { NOTE_MAX_CHAR } from '../../../../../models/fulfilment'
import {
  CreditCardDetectedError,
  NoteError,
  NoteTooLongError,
} from '../../../../shared/NoteInput'

export const NOTE_THRESHOLD = 50

export type LineItemFulfilmentNoteProps = {
  note: string
  isDisabled?: boolean
  onChange: (note: string) => void
  onErrorsChange?: (errors: NoteError[]) => void
}

export const useLineItemFulfilmentNoteModel = ({
  note,
  onErrorsChange,
}: LineItemFulfilmentNoteProps) => {
  const t = useT()
  const hasCreditCard = detectCreditCard(note)
  const noteTooLong = note.length > NOTE_MAX_CHAR
  const shouldShowCount = note.length >= NOTE_MAX_CHAR - NOTE_THRESHOLD
  const remainingChars = NOTE_MAX_CHAR - note.length
  const characterCountMessage =
    shouldShowCount &&
    (remainingChars >= 0
      ? t(
          '{chars, plural, one {{chars} remaining character} other {{chars} remaining characters}}',
          {
            _context: 'Input character limit message',
            _comment:
              '"chars" variable is replaced with the number of characters remaining. The "one" plural form is used for 1; "other" is used for 0 or more than 1 in English.',
            chars: remainingChars,
          }
        )
      : t(
          '{chars, plural, one {{chars} character over the limit} other {{chars} characters over the limit}}',
          {
            _context: 'Input character limit message',
            _comment:
              '"chars" variable is replaced with the number of characters over the limit. The "one" plural form is used for 1; "other" is used for more than 1 in English.',
            chars: Math.abs(remainingChars),
          }
        ))

  useEffect(() => {
    if (!onErrorsChange) return

    const errors: NoteError[] = []

    if (hasCreditCard) {
      errors.push(CreditCardDetectedError)
    }

    if (noteTooLong) {
      errors.push(NoteTooLongError)
    }

    onErrorsChange(errors)
  }, [hasCreditCard, noteTooLong, onErrorsChange])

  return {
    hasCreditCard,
    noteTooLong,
    shouldShowCount,
    characterCountMessage,
  }
}
